import appsignal from "@/plugins/appsignal";

// Diagnostics for reports of the app "freezing" (SUPP-9155). Two signals:
//
// 1. A heartbeat timer. If the main thread is blocked, the timer fires late;
//    a gap over BLOCKED_THRESHOLD_MS is reported once the thread recovers.
// 2. An operation marker in localStorage around long-running work (e.g. saving
//    the form). If the tab is killed while blocked the heartbeat never gets to
//    report, so a marker still present on the next load is reported instead.
//
// Everything here is best-effort and must never break the app.

const HEARTBEAT_MS = 1000;
const BLOCKED_THRESHOLD_MS = 3000;
// Gaps longer than this are most likely the device sleeping, not a freeze.
const MAX_REPORTABLE_GAP_MS = 5 * 60 * 1000;
const MARKER_KEY = "tailboard:activeOperation";

let currentOperation = null;

function safeLocalStorage(fn) {
  try {
    return typeof localStorage !== "undefined" ? fn(localStorage) : null;
  } catch (e) {
    return null;
  }
}

function report(action, message, tags) {
  try {
    const span = appsignal.createSpan();
    span.setNamespace("Freeze");
    span.setAction(action);
    span.setTags(
      Object.assign(
        {
          userAgent: navigator.userAgent,
          path: window.location.pathname
        },
        tags
      )
    );
    span.setError(new Error(message));
    appsignal.send(span);
  } catch (e) {
    // best-effort only
  }
}

function reportStaleMarker() {
  const raw = safeLocalStorage(storage => storage.getItem(MARKER_KEY));
  if (!raw) return;
  safeLocalStorage(storage => storage.removeItem(MARKER_KEY));

  let marker = {};
  try {
    marker = JSON.parse(raw);
  } catch (e) {
    // fall through with an empty marker
  }
  report(
    "Previous session ended mid-operation",
    `page closed while "${marker.name}" was running`,
    {
      operation: String(marker.name),
      startedAt: String(marker.startedAt),
      metadata: JSON.stringify(marker.metadata || {})
    }
  );
}

function startHeartbeat() {
  let lastTick = Date.now();

  // Hidden tabs have their timers throttled, which would look like a block.
  document.addEventListener("visibilitychange", () => {
    lastTick = Date.now();
  });

  setInterval(() => {
    const now = Date.now();
    const blockedMs = now - lastTick - HEARTBEAT_MS;
    lastTick = now;

    if (document.visibilityState !== "visible") return;
    if (blockedMs < BLOCKED_THRESHOLD_MS || blockedMs > MAX_REPORTABLE_GAP_MS)
      return;

    report("Main thread blocked", `main thread blocked for ${blockedMs}ms`, {
      blockedMs: String(blockedMs),
      operation: currentOperation ? currentOperation.name : "none"
    });
  }, HEARTBEAT_MS);
}

export function startFreezeMonitor() {
  try {
    reportStaleMarker();
    startHeartbeat();
  } catch (e) {
    // best-effort only
  }
}

// Wraps an async operation so a freeze during it can be attributed to it,
// and leaves a breadcrumb with its duration.
export async function trackOperation(name, metadata, fn) {
  const startedAt = Date.now();
  const operation = { name, startedAt, metadata };
  currentOperation = operation;
  safeLocalStorage(storage =>
    storage.setItem(MARKER_KEY, JSON.stringify(operation))
  );

  try {
    return await fn();
  } finally {
    if (currentOperation === operation) currentOperation = null;
    safeLocalStorage(storage => storage.removeItem(MARKER_KEY));
    appsignal.addBreadcrumb({
      category: "Operation",
      action: name,
      metadata: Object.assign({ durationMs: Date.now() - startedAt }, metadata)
    });
  }
}
